/*
 * Copyright (c) 2018. Unconventional Software Corporation
 */

import {Injectable, OnDestroy} from '@angular/core';
import 'rxjs/add/operator/map';
import {CompEvent} from "../models/CompEvent";
import {CompetitionService} from "./competition-service";
import {BehaviorSubject, Observable, Subject, Subscription} from "rxjs";
import {CompetitionMetadata} from "../models/schedule/metadata/CompetitionMetadata";
import {DataReference, EntryStatus} from "../shared/DataReference";
import {EntryScheduleItem} from "../models/schedule/EntryScheduleItem";
import {ScheduleItem} from "../models/schedule/ScheduleItem";
import {Studio} from "../models/Studio";
import {ScheduleBuilder} from "../models/schedule/ScheduleBuilder";
import * as moment from 'moment';
import DurationConstructor = moment.unitOfTime.DurationConstructor;
import {ScheduleFilterOptions} from "../shared/PageRefs";
import {EntryAward, EntryMedalAward} from "../models/awards/EntryAward";
import {FirebaseApp} from "angularfire2";
import {AngularFireDatabase} from "angularfire2/database";


@Injectable()
export class ScheduleService implements OnDestroy {

  // region Static Members

  private static SCHEDULE_ORDER: string = 'sequence';
  public static TIME_FORMAT: string = 'HH:mm:ss';
  public static DATE_FORMAT: string = 'YYYY-MM-DD';
  private static DURATION_DAY: DurationConstructor = 'd';

  // endregion

  // region Event Members

  private _selectedEvent$: BehaviorSubject<CompEvent> = new BehaviorSubject<CompEvent>(null);
  selectedEvent$ : Observable<CompEvent> = this._selectedEvent$.asObservable();

  get currentEvent() {
    return Object.assign({}, this._selectedEvent$.getValue());
  }

  private _eventDays: Array<string>;
  private _eventDays$: BehaviorSubject<Array<string>> = new BehaviorSubject([]);

  get eventDays() {
    return this._eventDays$.asObservable();
  }

  // endregion

  // region Metadata Members

  private _competitionMetadata$: BehaviorSubject<CompetitionMetadata> = new BehaviorSubject<CompetitionMetadata>(null);
  competitionMetadata$ = this._competitionMetadata$.asObservable();
  private competitionMetadata: CompetitionMetadata;
  private _metadataSubscription: Subscription;

  // endregion

  // region Studio Members

  private _studios$: BehaviorSubject<Array<Studio>> = new BehaviorSubject([]);

  get studios() {
    return this._studios$.asObservable();
  }

  // endregion

  // region Awards Members

  private _entryMedals$: BehaviorSubject<EntryMedalAward[]> = new BehaviorSubject([]);
  entryMedals$ = this._entryMedals$.asObservable();

  private _entryAwards$: BehaviorSubject<EntryAward[]> = new BehaviorSubject<EntryAward[]>([]);
  entryAwards$: Observable<EntryAward[]> = this._entryAwards$.asObservable();

  // endregion

  // region Filters

  private _selectedFilters$: BehaviorSubject<ScheduleFilterOptions> = new BehaviorSubject<ScheduleFilterOptions>({
    ages: {},
    types: {},
    categories: {},
    classifications: {},
    medals: {},
    isEnabled: true,
    isShowBreaks: true,
    isIncludeDancers: true,
    isIncludeStudios: true,
  });
  selectedFilters$: Observable<ScheduleFilterOptions> = this._selectedFilters$.asObservable();

  applyFilters(filters: ScheduleFilterOptions) {

    this._selectedFilters$.next(filters);
  }

  setSearch(value: string) {
    let currentSearch = Object.assign({}, this._selectedFilters$.getValue());
    currentSearch.searchText = value;
    this._selectedFilters$.next(currentSearch);
    console.log('updated filters')
  }

  // endregion

  // region Entry Members

  private _entriesSubscription: Subscription;
  public _entries: Array<ScheduleItem> = [];
  private _entries$: BehaviorSubject<Array<ScheduleItem>> = new BehaviorSubject<Array<ScheduleItem>>([]);

  activeEntryId$ = this._entries$
    .map(entries => entries.filter(entry => entry.entryStatus == EntryStatus.Active.valueOf()))
    .map(activeEntries => activeEntries.length > 0 ? activeEntries[0].id : null)
    .distinctUntilChanged();

  private _selectedEntry: Subject<EntryScheduleItem> = new Subject<EntryScheduleItem>();

  get selectedEntry() {
    return this._selectedEntry.asObservable();
  }

  get entries(): Observable<ScheduleItem[]> {
    return this._entries$.asObservable();
  }

  // endregion

  constructor(private competitionService: CompetitionService,
              private af: AngularFireDatabase,
              private firebase: FirebaseApp) {

  }

  // region Public Methods

  private _fakeSub: Subscription;

  loadEvent(event: CompEvent) {
    console.log('ScheduleService loadEvent');
    this._selectedFilters$.next({
      ages: {},
      types: {},
      categories: {},
      classifications: {},
      medals: {},
      isEnabled: true,
      isShowBreaks: true,
      isIncludeDancers: true,
      isIncludeStudios: true,
    });

    let lastEventId: string;

    if (this._selectedEvent$.getValue()) {
      lastEventId = this._selectedEvent$.getValue().id;
    }

    this._selectedEvent$.next(event);

    if (!lastEventId || lastEventId != event.id) {
      this._eventDays = [];
      this._eventDays$.next(this._eventDays);

      this.competitionService.getMetadata(event.competition.id, event.tourId).then(metadata => {
        // console.log(metadata);
        this._competitionMetadata$.next(metadata);
        this.competitionMetadata = metadata; // TODO: Do we need this?
        return metadata != null;
      }, err => {
        console.error(err);
      }).then((success) => {
        if (success) {
          this.loadEventDays();
          this.loadEntriesForEvent(event.id);
          this.loadStudiosForEvent(event.id);
          this.watchScoresForEvent(event.id);
          this.watchAwardsForEvent(event.id);

          this._fakeSub = this.getNumberOfUsersForEvent(event.id)
            .subscribe(x => console.log('***There are ' + x + ' users at this event***'));
        }
      });
    }
  }

  getNumberOfUsersForEvent(eventId: string) {
    return this.af.list('userObjects/eventsPurchased', ref => ref.orderByChild('luv2Dance2020_OrlandoRegional/dateAdded').startAt(1000))
      .snapshotChanges()
      .map(snap => snap.length);
  }

  loadStudiosForEvent(eventId: string) {
    this.firebase.database().ref(DataReference.getEventStudiosRef(eventId)).on(DataReference.Value, snapshot => {
      // console.log(snapshot.key + 'done');
      if (snapshot.exists()) {
        let studios = Studio.fromJsonArray(snapshot.val()).sort((a, b) => {
          return a.identifier < b.identifier ? -1 : a.identifier == b.identifier ? 0 : 1;
        });
        // console.log(studios);
        this._studios$.next(studios);
      }
    }, err => {
      console.error(err);
    });
  }

  selectEntry(entry: EntryScheduleItem) {
    this._selectedEntry.next(entry);
  }

  // endregion

  // region Loading Methods

  private loadEventDays() {
    this._eventDays = [];

    let end = moment(this._selectedEvent$.getValue().endDate);

    let next = moment(this._selectedEvent$.getValue().startDate);

    while (next.isSameOrBefore(end)) {
      this._eventDays.push(next.format(ScheduleService.DATE_FORMAT));
      next = next.add(1, ScheduleService.DURATION_DAY)
    }

    this._eventDays$.next(this._eventDays);
  }


  private loadEntriesForEvent(eventId: string) {
    this.resetEntries();

    this._entriesSubscription = this.af.list(DataReference.getEntriesRef(eventId), ref => ref.orderByChild(DataReference.ENTRY_STATUS).startAt(EntryStatus.Confirmed))
      .snapshotChanges(['child_added', 'child_removed', 'child_changed'])
      .debounceTime(200)
      .withLatestFrom(this._competitionMetadata$)
      .map(([entries, metadata]) => ScheduleBuilder.fromAngularFireSnapshots(entries, metadata, true))
      .subscribe(entries => {
        this._entries$.next(entries);
        // console.log(entries);
      });

  }

  private resetEntries() {
    this._entries = [];
    this._entries$.next([]);
  }

  // endregion


  // region Awards Methods

  private _medalSubscription: Subscription;
  private _awardSubscription: Subscription;

  private watchScoresForEvent(eventId: string) {
    console.log('Watching scores for event ' + eventId);
    this._medalSubscription = this.af.list(DataReference.getScoresForEvent(eventId),
        ref => ref.orderByChild(DataReference.PUBLISHED_KEY).equalTo(DataReference.PUBLISHED_VALUE))
      .snapshotChanges(['child_added', 'child_removed', 'child_changed'])
      .debounceTime(200)
      .withLatestFrom(this._competitionMetadata$)
      .map(([medals, metadata]) => EntryMedalAward.fromAngularFireSnapshots(medals, metadata))
      .subscribe(medals => {
        this._entryMedals$.next(medals);
        // console.log(medals);
      });
  }

  private watchAwardsForEvent(eventId: string) {
    console.log('Watching awards for event ' + eventId);

    this._awardSubscription = this.af.list(DataReference.getAwardsForEventRef(eventId),
        ref => ref.orderByChild(DataReference.PUBLISHED_KEY).equalTo(DataReference.PUBLISHED_VALUE))
      .snapshotChanges(['child_added', 'child_removed', 'child_changed'])
      .debounceTime(200)
      .map(awards => EntryAward.fromAngularFireSnapshots(awards))
      .subscribe(awards => {
        this._entryAwards$.next(awards);
        // console.log(awards);
      });
  }

  // endregion


  // region Lifecycle Methods

  ngOnDestroy(): void {
    if (this._metadataSubscription) {
      this._metadataSubscription.unsubscribe();
    }
    if (this._medalSubscription) {
      this._medalSubscription.unsubscribe();
    }
    if (this._awardSubscription) {
      this._awardSubscription.unsubscribe();
    }
    if (this._fakeSub) {
      this._fakeSub.unsubscribe();
    }
  }

  // endregion
}



// WEBPACK FOOTER //
// ./src/providers/schedule-service.ts